import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTasklist } from '@/context/TasklistContext';
import { useOverviewContext } from '@/context/OverviewContext';
import { useSchool } from '@/context/SchoolContext';
import { useSelectedStudent } from '@/context/SelectedStudentContext';
import type { LearningCenterTask } from '@/components/LearningCenter/LearningCenter';
import { applyTasklistPlaceholders } from '@/utils/tasklistPagePlaceholders';
import { shouldShowTasklistSlice } from '@/utils/tasklistPageDisplay';
import { useTasklistDisplayRuntime } from '@/hooks/useTasklistDisplayRuntime/useTasklistDisplayRuntime';
import {
  buildLearningCenterHubVisibleTasks,
  type LearningCenterHubSourceTask,
} from '@/utils/learningCenterHubRows';
import { resolveSecondaryTasklistPageJson } from '@/utils/resolveSecondaryTasklistPageJson';
import type { TasklistPageJson } from '@/types/tasklistPageJson';
import { studentPreviewRole } from '@/utils/toolSettingsUtils';

/**
 * Loads page JSON for hero + Learning Center tasks and filters by `displayConditions`.
 */
export function useFilteredTasklistTasks() {
  const { heroTasks, learningCenterTasks, tasklistData } = useTasklist();
  const { selectedStudent } = useSelectedStudent();
  const { userRoleVal } = useOverviewContext();
  const { runtime: displayRuntime, isReady: displayRuntimeReady } = useTasklistDisplayRuntime();
  const { data: schoolData } = useSchool();

  const [pageByPageId, setPageByPageId] = useState<Map<string, TasklistPageJson>>(() => new Map());
  const [resolvingPages, setResolvingPages] = useState(false);

  const pageIdsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const row of tasklistData) {
      const pid = row.pageId?.trim();
      if (pid) ids.add(pid);
    }
    for (const t of learningCenterTasks) {
      const pid = t.pageId?.trim();
      if (pid) ids.add(pid);
    }
    return [...ids].sort().join(',');
  }, [tasklistData, learningCenterTasks]);

  useEffect(() => {
    const ac = new AbortController();
    const ids = pageIdsKey ? pageIdsKey.split(',') : [];

    if (!ids.length) {
      setPageByPageId(new Map());
      setResolvingPages(false);
      return;
    }

    setResolvingPages(true);
    void Promise.all(
      ids.map((pid) =>
        resolveSecondaryTasklistPageJson({
          pageId: pid,
          schoolData,
          signal: ac.signal,
        }).then((json) => [pid, json] as const)
      )
    )
      .then((pairs) => {
        if (ac.signal.aborted) return;
        const next = new Map<string, TasklistPageJson>();
        for (const [pid, json] of pairs) {
          if (json) next.set(pid, json);
        }
        setPageByPageId(next);
      })
      .finally(() => {
        if (!ac.signal.aborted) setResolvingPages(false);
      });

    return () => ac.abort();
  }, [pageIdsKey, schoolData]);

  const placeholderVars = useMemo(
    () => ({
      studentName: selectedStudent?.nickName || selectedStudent?.firstName || 'your student',
      school: selectedStudent?.schoolShortName || selectedStudent?.schoolName || 'your school',
    }),
    [selectedStudent]
  );

  const applyPlaceholders = useCallback(
    (text: string) => applyTasklistPlaceholders(text, placeholderVars),
    [placeholderVars]
  );

  const visibilityCtx = useMemo(
    () => ({
      userRoleVal: studentPreviewRole(userRoleVal),
      gradeId: selectedStudent?.gradeId,
      studentAge: selectedStudent?.age,
      runtime: displayRuntimeReady ? displayRuntime : undefined,
    }),
    [userRoleVal, selectedStudent?.gradeId, selectedStudent?.age, displayRuntime, displayRuntimeReady]
  );

  const heroVisible = useCallback(
    (pageId: string | undefined) => {
      const pid = pageId?.trim();
      if (!pid) return true;
      const page = pageByPageId.get(pid);
      if (!page) return resolvingPages;
      if (page.isVisible === false) return false;
      return shouldShowTasklistSlice(
        page.displayConditions,
        visibilityCtx.userRoleVal,
        visibilityCtx.gradeId,
        visibilityCtx.runtime,
        visibilityCtx.studentAge
      );
    },
    [pageByPageId, visibilityCtx, resolvingPages]
  );

  const visibleHeroTasks = useMemo(() => {
    return heroTasks
      .filter((task) => {
        const row = tasklistData.find((r) => Number(r.checklistId) === Number(task.id));
        return heroVisible(row?.pageId);
      })
      .map((task) => {
        const row = tasklistData.find((r) => Number(r.checklistId) === Number(task.id));
        return {
          ...task,
          pageId: row?.pageId,
          pathSegment: row?.pathSegment,
        };
      });
  }, [heroTasks, tasklistData, heroVisible]);

  const hubSourceTasks: LearningCenterHubSourceTask[] = useMemo(
    () => learningCenterTasks.filter((t) => Boolean(t.pageId?.trim())),
    [learningCenterTasks]
  );

  const visibleLearningCenterTasks: LearningCenterTask[] = useMemo(() => {
    const rows = buildLearningCenterHubVisibleTasks(hubSourceTasks, pageByPageId, {
      ...visibilityCtx,
      applyPlaceholders,
    });
    return rows.map((v) => ({
      id: v.id,
      title: v.displayTitle,
      description: v.displayDescription,
      taskType: v.taskType,
      completed: v.completed,
      order: v.order,
      pageId: v.pageId,
      pathSegment: v.pathSegment,
      group: v.group,
      roles: v.roles,
    }));
  }, [hubSourceTasks, pageByPageId, visibilityCtx, applyPlaceholders]);

  return {
    visibleHeroTasks,
    visibleLearningCenterTasks,
    isResolvingPages: resolvingPages,
  };
}
